import React, {
  Dispatch,
  SetStateAction,
  useRef,
  RefObject,
  memo,
  Fragment,
  useMemo,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { Upload } from 'antd';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { useTakeNativeCameraPhoto } from 'src/components/domain/capacitor/hooks/use-take-native-camera-photo';
import { Icon } from 'src/components/general/icon';
import { AttachmentGhostInput } from 'src/components/general/inputs/ghost-input';
import { FlexColumn } from 'src/components/layout/flex-column';
import { InputUploadFile } from './contact-interaction-input-area-attachment-options';

const StyledUpload = styled(Upload)`
  display: none;
`;

type ContactInteractionFileUploadInputProps = {
  inputRef: RefObject<HTMLInputElement>;
  setAttachmentList: Dispatch<SetStateAction<InputUploadFile[]>>;
};

export const ContactInteractionFileUploadInput =
  memo<ContactInteractionFileUploadInputProps>(({ inputRef, setAttachmentList }) => {
    return (
      <StyledUpload
        openFileDialogOnClick={true}
        itemRender={() => null}
        iconRender={() => null}
        data-lgg-id="contact-interaction-file-upload-input"
        multiple
        children={<AttachmentGhostInput ref={inputRef} />}
        onChange={(e) => {
          const uploadFile = e.file.originFileObj;

          // This gets fired multiple times per each file upload, so we make sure to only add the file
          // to s3 once
          if (uploadFile && e.file.percent === 0) {
            setAttachmentList((list) => {
              return [
                ...list,
                {
                  file: uploadFile,
                  id: uploadFile.uid,
                  url: null,
                  attachmentType: null,
                  status: 'VALIDATING',
                  isRecentFile: false,
                },
              ];
            });
          }
        }}
      />
    );
  });

type ContactInteractionCameraUploadInputProps = {
  onChangeHandler: (file: File) => void;
};

export const ContactInteractionCameraUploadInput =
  memo<ContactInteractionCameraUploadInputProps>(({ onChangeHandler }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const takeNativeCameraPhoto = useTakeNativeCameraPhoto();

    const cameraButton = useMemo(() => {
      return match(Capacitor.isNativePlatform())
        .with(true, () => {
          const takePicture = async () => {
            const file = await takeNativeCameraPhoto();

            if (file) {
              onChangeHandler(file);
            }
          };

          return (
            <>
              <Icon type="camera" onClick={takePicture} />
            </>
          );
        })
        .otherwise(() => (
          <Fragment>
            <AttachmentGhostInput
              accept="image/*"
              id="icon-button-file"
              type="file"
              capture="environment"
              ref={inputRef}
              onChange={(e) => {
                const file = e.target.files?.[0];

                if (file) {
                  onChangeHandler(file);
                }
              }}
            />
            <Icon type="camera" onClick={() => inputRef.current?.click()} />
          </Fragment>
        ));
    }, [takeNativeCameraPhoto, onChangeHandler]);

    return <FlexColumn>{cameraButton}</FlexColumn>;
  });
